// src/services/api/admin.ts 
import { httpClient } from '../http/client';
import { ApiResponse, FrontendPaginatedResponse, PaginatedResponse } from '@/services/types/api';
import { NewUserPayload, Order, OrderData, RefundRequest } from '../types/domain';
import { User } from '@/types';

export interface SellerRequest {
    id: number;
    user_id: number;
    store_name: string;
    store_description?: string;
    business_license?: string;
    tax_id?: string;
    business_address?: string;
    verification_status: 'pending' | 'verified' | 'rejected';
    created_at: string;
    updated_at: string;
    user?: {
        id: number;
        username: string;
        email: string;
        first_name: string;
        last_name: string;
        phone?: string;
    };
}

export interface AdminDashboardStats {
    total_users: number;
    total_sellers: number;
    pending_sellers: number;
    total_orders: number;
    total_revenue: number;
    recent_activity: Array<{
        type: string;
        message: string;
        created_at: string;
    }>;
}

export interface SellerRequestFilters {
    status?: 'pending' | 'verified' | 'rejected' | 'all';
    search?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
}

class AdminApi {
    private readonly basePath = '/admin';
    /**
     * obtener las analiticas del sistema
     */
    async getAnalytics(filters: any): Promise<any> {
        const response = await httpClient.get<ApiResponse<any>>(
            `${this.basePath}/analytics`,
            { params: filters }
        );
        return response.data.data;
    }
    /**
     * Obtener estadísticas del dashboard de admin
     */
    async getDashboardStats(): Promise<AdminDashboardStats> {
        const response = await httpClient.get<ApiResponse<AdminDashboardStats>>(
            `${this.basePath}/dashboard`
        );
        return response.data.data;
    }

    /**
     * Obtener lista de solicitudes de vendedores
     */
    async getSellerRequests(filters: SellerRequestFilters = {}): Promise<
        FrontendPaginatedResponse
    /*{
        data: SellerRequest[];
        sellers: SellerRequest[];
        pagination: {
            current_page: number;
            total_pages: number;
            total_items: number;
            per_page: number;
        };
    }*/
    > {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== 'all') {
                params.append(key, value.toString());
            }
        });

        const queryString = params.toString();
        const url = `${this.basePath}/sellers${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<PaginatedResponse<any>>(url);
        //return response.data.data;
        return this.adaptBackendResponse(response.data);
    }

    /**
     * Obtener detalles de una solicitud de vendedor específica
     */
    async getSellerRequest(sellerId: number): Promise<SellerRequest> {
        const response = await httpClient.get<ApiResponse<SellerRequest>>(
            `${this.basePath}/sellers/${sellerId}`
        );
        return response.data.data;
    }

    /**
     * Aprobar una solicitud de vendedor
     */
    async approveSeller(sellerId: number): Promise<SellerRequest> {
        const response = await httpClient.put<ApiResponse<SellerRequest>>(
            `${this.basePath}/sellers/${sellerId}/approve`
        );
        return response.data.data;
    }

    /**
     * Rechazar una solicitud de vendedor
     */
    async rejectSeller(sellerId: number, reason?: string): Promise<SellerRequest> {
        const response = await httpClient.put<ApiResponse<SellerRequest>>(
            `${this.basePath}/sellers/${sellerId}/status`,
            {
                status: 'rejected',
                rejection_reason: reason
            }
        );
        return response.data.data;
    }

    /**
     * Actualizar estado de un vendedor (verificado, rechazado, suspendido)
     */
    async updateSellerStatus(
        sellerId: number,
        status: 'verified' | 'rejected' | 'suspended',
        notes?: string
    ): Promise<SellerRequest> {
        const response = await httpClient.put<ApiResponse<SellerRequest>>(
            `${this.basePath}/sellers/${sellerId}/status`,
            {
                status,
                admin_notes: notes
            }
        );
        return response.data.data;
    }
    async createUser(payload: NewUserPayload): Promise<User> {
        const res = await httpClient.post<ApiResponse<User>>(`${this.basePath}/users`, payload);
        return res.data.data;
    }
    /**
     * Obtener lista de usuarios
     */
    async getUsers(filters: {
        user_type?: 'customer' | 'seller' | 'admin';
        status?: 'active' | 'inactive' | 'suspended' | 'banned';
        search?: string;
        page?: number;
        per_page?: number;
    } = {}): Promise<
        FrontendPaginatedResponse
    > {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, value.toString());
            }
        });

        const queryString = params.toString();
        const url = `${this.basePath}/users${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<PaginatedResponse<any>>(url);
        //return response.data.data;
        return this.adaptBackendResponse(response.data);
    }
    async updateUserStatus(userId: number, status: 'active' | 'inactive' | 'suspended' | 'banned'): Promise<any> {
        const response = await httpClient.put<ApiResponse<any>>(
            `${this.basePath}/users/${userId}/status`,
            {
                status
            }
        );
        return response.data.data;
    }
    /**
     * Obtener todas las órdenes (admin)
     */
    async getAllOrders(filters: {
        status?: string;
        payment_status?: string;
        date_from?: string;
        date_to?: string;
        search?: string;
        page?: number;
        per_page?: number;
    } = {}): Promise<
        FrontendPaginatedResponse
    > {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, value.toString());
            }
        });

        const queryString = params.toString();
        const url = `${this.basePath}/orders${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<PaginatedResponse<any>>(url);
        //return response.data;
        return this.adaptBackendResponse(response.data);
    }

    /**
     * Actualizar estado de una orden (solo admin)
     */
    async updateOrderStatus(
        orderId: number,
        status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded',
        notes?: string
    ): Promise<any> {
        const response = await httpClient.put<ApiResponse<any>>(
            `${this.basePath}/orders/${orderId}/status`,
            {
                status,
                notes
            }
        );
        return response.data.data;
    }
    async updateOrderPaymentStatus(
        orderId: number,
        payment_status: "pending" | "paid" | "partially_refunded" | "failed" | "refunded",
        note?: string
    ): Promise<OrderData> {
        const res = await httpClient.put<ApiResponse<OrderData>>(
            `${this.basePath}/orders/${orderId}/payment-status`,
            { payment_status, note }
        );
        return res.data.data; // ← devolvemos sólo la data limpia
    }
    async reviewRefundRequest(
        orderId: number,
        decision: "approved" | "rejected",
        options?: { note?: string; refund_amount?: number }
    ): Promise<Order> {
        const res = await httpClient.post<ApiResponse<Order>>(`/admin/orders/${orderId}/refund-review`, {
            decision,
            ...options,
        });
        return res.data.data;
    }
    async updateReviewRefundRequest(
        id: number,
        status: "approved" | "rejected",
        options?: { note?: string; refund_amount?: number }
    ): Promise<RefundRequest> {
        const res = await httpClient.put<ApiResponse<RefundRequest>>(`/admin/refund-requests/${id}/status`, {
            status,
            ...options,
        });
        return res.data.data;
    }
    async getRefundRequestForOrder(orderId: number): Promise<RefundRequest> {
        const res = await httpClient.get<ApiResponse<RefundRequest>>(`/admin/orders/${orderId}/refund-request`);
        return res.data.data;
    }

    /**
     * Alternativa por ID (si el listado te trae refund_request_id)
     */
    async getRefundRequestById(refundRequestId: number): Promise<RefundRequest> {
        const res = await httpClient.get<ApiResponse<RefundRequest>>(`/refund-requests/${refundRequestId}`);
        return res.data.data;
    }
    // Obtener detalle para la vista de “ver”
    async getOrderById(orderId: number): Promise<Order> {
        const res = await httpClient.get<ApiResponse<Order>>(`/admin/orders/${orderId}`);
        return res.data.data;
    }
    /**
     * Obtener reseñas para moderación
     */
    async getReviews(filters: {
        rating?: number;
        //status?: 'pending' | 'approved' | 'rejected';
        status?: string;
        is_approved?: boolean;
        product_id?: number;
        search?: string;
        page?: number;
        per_page?: number;
    } = {}): Promise<FrontendPaginatedResponse> {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, value.toString());
            }
        });

        const queryString = params.toString();
        const url = `${this.basePath}/reviews${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<PaginatedResponse<any>>(url);
        //return response.data.data;
        return this.adaptBackendResponse(response.data);
    }

    /**
     * Aprobar o rechazar una reseña
     */
    async updateReviewStatus(reviewId: number, approved: boolean): Promise<any> {
        const response = await httpClient.put<ApiResponse<any>>(
            `${this.basePath}/reviews/${reviewId}/status`,
            { is_approved: approved }
        );
        return response.data.data;
    }
    // Si tu backend expone este endpoint:
    async getSellerStoreByUser(userId: number) {
        const res = await httpClient.get<ApiResponse<any>>(`/admin/users/${userId}/store`);
        return res.data.data; // { ...store, products: [...] }
    }

    // Opcional: por sellerId (si prefieres esta ruta):
    async getSellerStore(sellerId: number) {
        const res = await httpClient.get<ApiResponse<any>>(`/admin/sellers/${sellerId}/full`);
        return res.data.data;
    }
    async setProductFeatured(productId: number, featured: boolean): Promise<void> {
        await httpClient.put<ApiResponse<any>>(
            `${this.basePath}/products/${productId}`,
            { is_featured: featured ? 1 : 0 }
        );
        // Si tu backend devuelve el producto actualizado, puedes retornar res.data.data y
        // usarlo en la UI en vez de hacer update optimista.
    }
    /**
     * Exportar datos (CSV, Excel)
     */
    async exportData(
        type: 'sellers' | 'users' | 'orders' | 'reviews',
        format: 'csv' | 'excel' = 'csv',
        filters: any = {}
    ): Promise<Blob> {
        const params = new URLSearchParams();
        params.append('format', format);

        Object.entries(filters).forEach(([key, value]) => {
            if (!value) return;
            if (value !== undefined) {
                params.append(key, value.toString());
            }
        });

        const response = await httpClient.get(
            `${this.basePath}/export/${type}?${params.toString()}`,
            { responseType: 'blob' }
        );

        return response.data;
    }
    private adaptBackendResponse<T>(backendResponse: PaginatedResponse<T>): FrontendPaginatedResponse<T> {
        const { data, pagination } = backendResponse;

        return {
            data,
            meta: {
                current_page: pagination.current_page,
                from: (pagination.current_page - 1) * pagination.per_page + 1,
                last_page: pagination.total_pages,
                per_page: pagination.per_page,
                to: Math.min(pagination.current_page * pagination.per_page, pagination.total),
                total: pagination.total,
            },
            links: {
                first: `?page=1`,
                last: `?page=${pagination.total_pages}`,
                prev: pagination.current_page > 1 ? `?page=${pagination.current_page - 1}` : undefined,
                next: pagination.has_more ? `?page=${pagination.current_page + 1}` : undefined,
            },
        };
    }

}

// Instancia única del servicio
export const adminApi = new AdminApi();
export default adminApi;